/* =========================================================
   El progreso de una ficha concreta, resuelto contra los datos del portal.

   `progreso.ts` es aritmética pura: le das las fichas y te da el número.
   Esto es lo que sabe DÓNDE están esas fichas — qué instructores cuelgan
   de un site, qué sites de una organización, qué documentos tiene.

   Va aparte para que el cálculo se pueda probar sin montar una fuente de
   datos, y para que las ocho pantallas que enseñan un porcentaje no tengan
   que rearmar cada una la misma búsqueda.
   ========================================================= */
import type { Organization, Site } from '@/types';
import { ds } from '@/data';
import { progresoDeOrg, progresoDeSite, type Progreso } from './progreso';

/** Sin dato guardado todavía: se enseña 0 de 0, no un número inventado. */
const SIN_DATO: Progreso = { hechos: 0, total: 0, pct: 0 };

/** El porcentaje guardado en la base, si esta fuente lo guarda.
 *
 *  Con servidor, las pantallas enseñan SIEMPRE lo que guardó el servidor, y
 *  el navegador no calcula nada: ETC lo pidió así —la realidad registrada
 *  en la base, no una estimación de quien mira—. El borrador que se está
 *  escribiendo no mueve el número hasta que se guarda.
 *
 *  Solo la demostración, que no tiene servidor, lo calcula en memoria. */
function guardado(entidad: 'organization' | 'site', id: string): Progreso | null {
  if (!ds.guardaProgreso) return null;
  const g = ds.progresoGuardado(entidad, id);
  return g ? { hechos: g.hechos, total: g.total, pct: g.pct } : SIN_DATO;
}

/** Los instructores afiliados a un site. */
function instructoresDe(siteId: string) {
  const ids = new Set(ds.listAssignments({ siteId }).map((a) => a.InstructorId));
  return ds.listInstructors().filter((i) => ids.has(i.InstructorId));
}

/** El progreso de un site: su ficha y la de sus instructores.
 *
 *  Sin compliance: los documentos son de la organización.
 *
 *  `borrador` solo cuenta en la demostración, que calcula en memoria. Con
 *  servidor manda lo guardado en la base. */
export function progresoSite(
  siteId: string, borrador?: Record<string, unknown> | null,
): Progreso {
  const deLaBase = guardado('site', siteId);
  if (deLaBase) return deLaBase;
  const enMemoria = ds.listSites().find((s) => s.SiteId === siteId);
  if (!enMemoria) return { hechos: 0, total: 0, pct: 100 };
  // el borrador se superpone al guardado: trae solo lo tocado
  const site = borrador ? { ...enMemoria, ...borrador } as Site : enMemoria;
  return progresoDeSite(site, instructoresDe(siteId), ds.marcasNA());
}

/** El progreso de una organización: lo suyo, sus sites, sus instructores y
 *  sus documentos, en un solo número.
 *
 *  Un instructor que trabaje en dos sites del mismo socio cuenta una vez.
 *  Contarlo dos inflaría el denominador y haría que el mismo trabajo
 *  valiese el doble. */
export function progresoOrg(
  orgId: string, borrador?: Record<string, unknown> | null,
): Progreso {
  const deLaBase = guardado('organization', orgId);
  if (deLaBase) return deLaBase;
  const enMemoria = ds.listOrgs().find((o) => o.OrgId === orgId);
  if (!enMemoria) return { hechos: 0, total: 0, pct: 100 };
  const org = borrador ? { ...enMemoria, ...borrador } as Organization : enMemoria;
  const sites = ds.listSites({ orgId });
  const idsDeSusSites = new Set(sites.map((s) => s.SiteId));
  const suyos = new Set(
    ds.listAssignments()
      .filter((a) => idsDeSusSites.has(a.SiteId))
      .map((a) => a.InstructorId),
  );
  return progresoDeOrg(
    org,
    sites,
    ds.listInstructors().filter((i) => suyos.has(i.InstructorId)),
    ds.getCompliance(orgId),
    ds.marcasNA(),
  );
}
